import { Box, Container } from '@mui/material'
import { CarrierType } from '@operations/app/api'
import { useOperationsStore } from '@operations/AppProvider'
import { StoppagesHeader } from '@operations/features/stoppage/components/organisms/StoppagesHeader'
import { StoppageTable } from '@operations/features/stoppage/components/organisms/StoppageTable'
import { StoppageViewUIStore } from '@operations/features/stoppage/stores/stoppage-view.ui-store'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useParams } from 'react-router'

export const StoppagesPage = withAuthenticationRequired(
  observer(() => {
    const { stoppageStore, carrierVisitStore, appStore } = useOperationsStore()
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const { id } = useParams<{ id: string }>()

    const visitId = Number(id)

    useAsyncFetch(async () => {
      await Promise.all([
        carrierVisitStore.loadOne(visitId),
        stoppageStore.fetchDataByVisitId(visitId),
      ])

      stoppageStore.setSelectedVisitId(visitId)
    }, [stoppageStore, carrierVisitStore, visitId])

    const stoppageViewUIStore = useMemo(
      () => new StoppageViewUIStore(stoppageStore),
      [stoppageStore],
    )

    const handleDelete = async () => {
      if (!stoppageViewUIStore.stoppageToBeDeletedId) return

      try {
        await stoppageStore.onDelete(stoppageViewUIStore.stoppageToBeDeletedId)
        stoppageViewUIStore.toggleDeleteDialog()

        appStore.setShowAlert('success', t('stoppageDeleted', 'Stoppage deleted'))
      } catch (e) {
        appStore.setShowAlert(
          'error',
          t('stoppageReasonDeletionFailed', 'Stoppage reason deletion failed'),
        )
      }
    }

    return (
      <Box
        sx={{
          bgcolor: theme.palette.grey[100],
          minHeight: '100%',
        }}
      >
        <StoppagesHeader
          visitId={visitId}
          stoppageViewUIStore={stoppageViewUIStore}
          carrierVisitStore={carrierVisitStore}
        />

        <Container sx={{ paddingY: 2 }}>
          <StoppageTable
            stoppages={stoppageViewUIStore.stoppages}
            vesselVisitId={visitId}
            isVesselVisit={
              carrierVisitStore.items.find(v => v.id === visitId)?.type === CarrierType.Vessel
            }
            onDelete={id => stoppageViewUIStore.toggleDeleteDialog(id)}
          />

          <ConfirmationDialog
            open={stoppageViewUIStore.isDeleteDialogOpen}
            title={t('deleteStoppage', 'Delete stoppage')}
            message={t(
              'entityWillBeDeleted',
              '{name} will be deleted and you cannot undo this action!',
              {
                name: t('stoppage', 'Stoppage'),
              },
            )}
            primaryActionText={t('delete', 'Delete')}
            closeLabel={t('cancel', 'Cancel')}
            maxWidth='xs'
            onConfirm={() => handleDelete()}
            onClose={() => stoppageViewUIStore.toggleDeleteDialog()}
          />
        </Container>
      </Box>
    )
  }),
)
